/**
 * 夥伴管理（PRD §G / AC-G-1 / AC-G-6）— 清單 + 建立流程。
 *
 * 建立成功後的金鑰交付走 {@link SecretHandoffModal}：後端只存 sha256，明碼僅在
 * 建立回應出現一次，所以那個視窗刻意關不掉，必須明確確認。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import {
  Field,
  ModalShell,
  errorTextStyle,
  inputStyle,
  useEscapeToClose,
} from '../components/Modal';
import { Pill } from '../components/Pill';
import { SecretHandoffModal } from '../components/SecretHandoff';
import {
  ApiError,
  createPartner,
  listPartners,
  PARTNER_TIER_DEFAULTS,
  type Partner,
  type PartnerTier,
  type PartnerWithSecret,
} from '../lib/api-client';
import { formatQuota, TIER_LABEL, TIER_TONE } from '../lib/partner-format';

/** partner_code 進 URL 與設定檔，限縮成不需跳脫的字元集。 */
const CODE_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;

export function PartnersListPage(): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<PartnerWithSecret | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['partners.list'],
    queryFn: listPartners,
  });

  const partners: Partner[] = data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">夥伴管理（G）</h1>
        <div className="page-head__actions">
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)}>
            + 註冊夥伴
          </Button>
        </div>
      </div>

      <p className="page-intro">
        合作廠商（旅行社／OTA／通路商）的 API 帳號。註冊後配發 API Key，廠商即可串接商品、班次與定價
        API。配額依級別自動套用；點列進詳情可設定 Webhook、班次配額與停權。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          {(error as ApiError).status === 403 && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              此頁需 <code>admin.partner</code> 權限（預設僅 superadmin）。
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>代碼</th>
                <th>名稱</th>
                <th>級別</th>
                <th>狀態</th>
                <th>Webhook</th>
                <th style={{ textAlign: 'right' }}>每分鐘配額</th>
                <th style={{ textAlign: 'right' }}>每月配額</th>
                <th style={{ width: 60 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-cell">
                    尚無夥伴；點右上「註冊夥伴」建立第一個。
                  </td>
                </tr>
              )}
              {partners.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.partner_code}</td>
                  <td>{p.display_name}</td>
                  <td>
                    <Pill tone={TIER_TONE[p.tier]}>{TIER_LABEL[p.tier] ?? p.tier}</Pill>
                  </td>
                  <td>
                    {p.is_active ? (
                      <Pill tone="success">啟用中</Pill>
                    ) : (
                      <Pill tone="danger">已停權</Pill>
                    )}
                    {!p.is_active && p.suspended_reason && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        {p.suspended_reason}
                      </div>
                    )}
                  </td>
                  <td>
                    {!p.webhook_url ? (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>未設定</span>
                    ) : p.webhook_paused ? (
                      <Pill tone="warning">已暫停</Pill>
                    ) : (
                      <Pill tone="success">正常</Pill>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatQuota(p.quota_per_minute)}</td>
                  <td style={{ textAlign: 'right' }}>{formatQuota(p.quota_per_month)}</td>
                  <td>
                    <Link to={`/admin/partners/${p.id}`}>詳情</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div className="list-foot">
          <div>共 {partners.length} 個夥伴</div>
        </div>
      )}

      {creating && (
        <CreatePartnerModal
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setCreating(false);
            setIssued(p);
          }}
        />
      )}

      {issued && (
        <SecretHandoffModal
          title="夥伴已建立"
          subtitle={
            <>
              <strong>{issued.display_name}</strong>（
              <code style={{ fontFamily: 'monospace' }}>{issued.partner_code}</code>）·{' '}
              {TIER_LABEL[issued.tier]}
            </>
          }
          warning={
            <>
              <strong>此金鑰只會顯示這一次。</strong>
              系統僅保存雜湊值，關閉後無法再次查看。若遺失，只能重新產生一把新的，而舊金鑰會立即失效
              —— 廠商已上線的串接會中斷。
            </>
          }
          secretLabel="API Key"
          secret={issued.api_key}
          details={
            <>
              <dt>OAuth Client ID</dt>
              <dd style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {issued.oauth_client_id}
              </dd>
              <dt>配額</dt>
              <dd>
                每分鐘 {formatQuota(issued.quota_per_minute)} 次、每月{' '}
                {formatQuota(issued.quota_per_month)} 次
              </dd>
            </>
          }
          onAcknowledge={() => setIssued(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 建立表單

function CreatePartnerModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: PartnerWithSecret) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [tier, setTier] = useState<PartnerTier>('T1');

  const mutation = useMutation({
    mutationFn: createPartner,
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['partners.list'] });
      onCreated(p);
    },
  });

  const trimmedCode = code.trim();
  const trimmedName = name.trim();
  const codeError =
    trimmedCode.length > 0 && !CODE_PATTERN.test(trimmedCode)
      ? '僅能使用英數字、底線與連字號，且須以英數字開頭'
      : trimmedCode.length > 0 && trimmedCode.length < 2
        ? '至少 2 個字元'
        : null;
  const canSubmit =
    trimmedCode.length >= 2 && !codeError && trimmedName.length > 0 && !mutation.isPending;

  useEscapeToClose(onClose, !mutation.isPending);

  const quota = PARTNER_TIER_DEFAULTS[tier];

  return (
    <ModalShell onBackdropClick={mutation.isPending ? undefined : onClose} width={520}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>註冊夥伴</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
        建立後會配發 API Key，明碼僅顯示一次。
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          mutation.mutate({ partner_code: trimmedCode, display_name: trimmedName, tier });
        }}
      >
        <Field label="夥伴代碼" hint="系統識別用，建立後不可修改">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="taipei-travel"
            style={{ ...inputStyle, fontFamily: 'monospace' }}
            autoFocus
          />
          {codeError && <div style={errorTextStyle}>{codeError}</div>}
        </Field>

        <Field label="顯示名稱" hint="對內辨識用的中文全名">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="台北旅行社股份有限公司"
            style={inputStyle}
          />
        </Field>

        <Field label="級別" hint="決定 API 配額；日後可調整">
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as PartnerTier)}
            style={inputStyle}
          >
            {(Object.keys(TIER_LABEL) as PartnerTier[]).map((t) => (
              <option key={t} value={t}>
                {TIER_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>

        <div
          style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            color: '#374151',
            marginBottom: 16,
          }}
        >
          {TIER_LABEL[tier]} 配額：每分鐘 {quota.quotaPerMinute.toLocaleString('zh-TW')} 次、每月{' '}
          {quota.quotaPerMonth.toLocaleString('zh-TW')} 次、同時保留 {quota.maxActiveHolds} 筆、
          保留時效 {quota.holdTtlSeconds / 60} 分鐘
        </div>

        {/* 代碼重複時後端回 400 + 「partner_code 已存在：xxx」，訊息本身已足夠明確 */}
        {mutation.isError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {(mutation.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            取消
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? '建立中…' : '建立並配發金鑰'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
